/**
 * OPERATIONS — WHAT THE MACHINE IS DOING, AND WHAT IT HAS FAILED TO DO.
 *
 * Three things a person needs to see when something has gone quiet: the
 * messages that would not send, the scheduled jobs and whether they ran, and
 * the individual periods a job owes work for (0046) with every attempt it has
 * made at each.
 *
 * A failed message says WHY it failed, because "failed" alone sends somebody to
 * the logs. "No provider is configured" is a setting; "the number is not on
 * WhatsApp" is the customer's; they look identical on a dashboard that counts
 * failures and different to anybody who has to fix one.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun } from '../hooks.js';
import { Banner, Button, Card, Kpi, Loading, Pill, Register, Select, Text, TextArea, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { count, today, words, instant } from '../format.js';
import { findingWords } from '../standing.js';
import { screenById } from '../../../src/screens/contracts.js';

/** 10 Oct (QA ISSUE-004/014): "3,600s" is a machine's word; a person reads "every hour". */
function everyWords(seconds: number): string {
  if (seconds % 86_400 === 0) { const d = seconds / 86_400; return d === 1 ? 'every day' : `every ${d} days`; }
  if (seconds % 3_600 === 0) { const h = seconds / 3_600; return h === 1 ? 'every hour' : `every ${h} hours`; }
  if (seconds % 60 === 0) { const m = seconds / 60; return m === 1 ? 'every minute' : `every ${m} minutes`; }
  return `every ${seconds}s`;
}

const MESSAGES = screenById('operations')!.messages;

export function Operations({ session }: { session: Session }) {
  const [tab, setTab] = useState<'outbox' | 'jobs' | 'findings'>('outbox');
  return (
    <Page title="Operations" sub="The machinery: messages the system is sending, the work it does on a schedule, and what its own checks found. Nothing here is a daily job — an empty screen is the system working.">
      <div className="segmented">
        <button type="button" className={tab === 'outbox' ? 'on' : ''} onClick={() => setTab('outbox')}>Messages</button>
        <button type="button" className={tab === 'jobs' ? 'on' : ''} onClick={() => setTab('jobs')}>Scheduled work</button>
        <button type="button" className={tab === 'findings' ? 'on' : ''} onClick={() => setTab('findings')}>Needs a look</button>
      </div>
      {tab === 'outbox' ? <Outbox session={session} /> : tab === 'jobs' ? <Jobs /> : <Findings session={session} />}
    </Page>
  );
}

/**
 * WHAT THE CHECKS FOUND, AND WHAT SOMEBODY DID ABOUT IT (26 Sep).
 *
 * The self-check has written findings since 0008 and Home showed four of them
 * with no detail, no page two and no way to answer one — an alarm ringing where
 * nobody could reach it. This is the whole open queue, worst first, each saying
 * what was expected against what is actually there, and each answerable in
 * words. Nothing here repairs anything: the check verifies and a person acts,
 * which is the whole design (D127).
 */
function Findings({ session }: { session: Session }) {
  type Severity = 'critical' | 'high' | 'medium';
  const [severity, setSeverity] = useState<Severity | ''>('');
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('findings.list', { ...(severity ? { severity } : {}), page, pageSize: 50 }), [severity, page], MESSAGES);
  const [answering, setAnswering] = useState<number | null>(null);
  const may = can(session, 'selfcheck.acknowledge');

  type Row = { findingId: number; severity: string; checkName: string; entity: string | null; detail: string | null;
               expected: string | null; actual: string | null; foundOn: string | null; dueBy: string | null;
               overdue: boolean; daysOverdue: number };
  const columns: readonly Column<Row>[] = [
    { key: 'what', head: 'What was found', cell: (r) => <><strong>{findingWords(r.checkName)}</strong>{r.detail ? <div className="muted">{r.detail}</div> : null}</> },
    { key: 'sev', head: 'How serious', cell: (r) => <Pill tone={r.severity === 'critical' ? 'danger' : r.severity === 'high' ? 'empty' : 'muted'}>
        {r.severity === 'critical' ? 'serious' : r.severity}</Pill> },
    { key: 'gap', head: 'Should be / is', cell: (r) => r.expected === null && r.actual === null ? <span className="muted">—</span>
        : <>{r.expected ?? '—'}<div className="muted">but: {r.actual ?? '—'}</div></> },
    { key: 'due', head: 'Answer by', cell: (r) => <>{r.dueBy ?? '—'}
        {r.overdue ? <div className="muted" style={{ color: 'var(--danger)' }}>{r.daysOverdue === 1 ? '1 day late' : `${r.daysOverdue} days late`}</div> : null}</> },
    { key: 'act', head: '', cell: (r) => may
        ? <Button small onClick={() => setAnswering((v) => v === r.findingId ? null : r.findingId)}>{answering === r.findingId ? 'Close' : 'Say what you did'}</Button>
        : null },
  ];

  return (
    <>
      <div className="toolbar">
        <Select name="severity" label="How serious" value={severity} onChange={(v) => { setSeverity(v as Severity | ''); setPage(1); }} placeholder="All"
                options={[{ value: 'critical', label: 'Serious' }, { value: 'high', label: 'High' }, { value: 'medium', label: 'Medium' }]} />
      </div>
      <Card>
        {asked.loading && !asked.data ? <Loading what="the findings" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows}
                        empty="The checks found nothing outstanding."
 />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
      {answering !== null ? <Answer findingId={answering} onDone={() => { setAnswering(null); asked.reload(); }} /> : null}
    </>
  );
}

function Answer({ findingId, onDone }: { findingId: number; onDone: () => void }) {
  const [note, setNote] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('finding.acknowledge', { findingId, note: note.trim() }));
    if (ok) onDone();
  }
  return (
    <Card title="What you did about it" sub="this answers the finding and takes it off the queue — it does not change the records it is about">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="info" title="Fix the records first, then say what you did."
              detail="Answering a finding is not correcting it: the check will find it again on its next run if nothing was actually put right." />
      <TextArea name="finding-note" label="What you did about it" value={note} onChange={setNote} />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !note.trim()}>
        {runner.busy ? 'Saving…' : 'Answer it'}
      </Button>
    </Card>
  );
}

/**
 * HOW THE QUEUE STANDS, ABOVE THE LIST OF WHAT IS IN IT (Slice 2).
 *
 * The list was all there was, and it could be paged through quite happily while 102
 * messages sat parked for months: fifty of them notices about papers already issued,
 * sixteen of them documents nobody could produce, every one of them labelled "waiting for
 * a person". A list of fifty rows, newest first, looks exactly the same whether the queue
 * is healthy or has been stuck since March.
 *
 * So the first thing on this tab is the ANSWER — is anything stuck, and for how long —
 * and the loudest line on it is a document somebody asked for and nothing produced,
 * because that is the failure that hid for the life of the project by looking tidy.
 *
 * Every figure comes from the server, computed over the WHOLE queue. A total added up from
 * the visible page would be the one number on this screen nobody could trust.
 */
function QueueHealth() {
  const asked = useAsk(() => api.view('queue.health', {}), [], MESSAGES);
  if (asked.loading) return <Card title="How the queue stands"><Loading what="the queue" /></Card>;
  if (asked.error) {
    // NOT AN EMPTY PANEL. "We could not read the queue" and "the queue is empty" are
    // opposite facts and a blank card reads as the second.
    return <Card title="How the queue stands"><Banner kind="warn" title={asked.error.title}
      {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;
  }
  const h = asked.data!;
  const t = h.totals;
  return (
    <Card title="How the queue stands" sub="counted over the whole queue, not the page below" className="queue-health">
      {h.alerts.map((a, i) => (
        <Banner key={i} kind={a.severity === 'error' ? 'error' : 'warn'} title={a.says} />
      ))}
      {h.alerts.length === 0 ? <p className="good-line">Nothing is stuck.</p> : null}
      <div className="kpis" style={{ marginTop: 10 }}>
        <Kpi label="Waiting to go" value={count(t.queued)} tone="empty" note="the worker will take these" />
        <Kpi label="Waiting for a person" value={count(t.awaitingHand)} tone="testing" note="nothing happens until somebody acts" />
        <Kpi label="Not produced" value={count(t.documentsNotProduced)} tone={t.documentsNotProduced > 0 ? 'danger' : 'muted'}
             note="documents asked for and never made" />
        <Kpi label="Failed" value={count(t.failed)} tone={t.failed > 0 ? 'danger' : 'muted'} note="with the reason kept" />
        <Kpi label="Recorded" value={count(t.recorded)} tone="muted" note="notes nobody acts on" />
      </div>
      <Register
        columns={[
          { key: 'what', head: 'Message', cell: (r: typeof h.rows[number]) => <>{r.what}<div className="muted">{r.kind}</div></> },
          { key: 'channel', head: 'Who acts on it', cell: (r: typeof h.rows[number]) =>
              r.channel === 'external' ? `a provider (${r.medium ?? '—'}), or a person`
              : r.channel === 'internal' ? `a renderer (${r.renderer ?? '—'}) — nobody can do it by hand`
              : 'nobody — it is a record' },
          { key: 'status', head: 'Standing', cell: (r: typeof h.rows[number]) =>
              <Pill tone={r.status === 'failed' ? 'danger' : r.status === 'awaiting_hand' ? 'testing' : r.status === 'delivered' ? 'good' : 'empty'}>
                {r.status === 'awaiting_hand' ? 'waiting for a person' : r.status}</Pill> },
          { key: 'n', head: 'How many', right: true, cell: (r: typeof h.rows[number]) => count(r.messages) },
          { key: 'oldest', head: 'Oldest', cell: (r: typeof h.rows[number]) =>
              r.oldestDays === null ? '—' : r.oldestDays === 0 ? 'today' : `${count(r.oldestDays)} days` },
          // WHY, and WHAT TO DO. A count with no reason sends somebody to the logs, and a
          // reason with no instruction leaves him reading it. Both come from the server, in
          // one place, so this screen and the alerts above it cannot say different things.
          { key: 'why', head: 'What went wrong', cell: (r: typeof h.rows[number]) =>
              r.failureReason ? <span className="muted">{r.failureReason}</span> : <span className="muted">—</span> },
          { key: 'next', head: 'What to do next', cell: (r: typeof h.rows[number]) =>
              r.nextAction ? r.nextAction : <span className="muted">nothing — wait</span> },
        ]}
        rows={h.rows} empty="There has never been anything in this queue." />
    </Card>
  );
}

function Outbox({ session }: { session: Session }) {
  type OutboxStatus = 'queued' | 'processing' | 'delivered' | 'failed' | 'awaiting_hand' | 'recorded';
  // 0080 — the tab opens on the messages a PERSON must send, because those are
  // the ones where nothing happens at all until somebody acts. Failures are one
  // click away and were what this opened on before there was a hand queue.
  const [status, setStatus] = useState<OutboxStatus | ''>('awaiting_hand');
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('outbox.list', { ...(status ? { status } : {}), page, pageSize: 50 }), [status, page], MESSAGES);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [sending, setSending] = useState<number | null>(null);
  const may = can(session, 'outbox.retry');
  const mayHand = can(session, 'outbox.send_by_hand');

  type Row = { messageId: number; kind: string; status: string; attempts: number; lastError: string | null;
               createdAt: string | null; nextTryAt: string | null; deliveredAt: string | null; failedAt: string | null;
               awaitingHandSince: string | null; sentByHandHow: string | null; sentByHandBy: string | null;
               sentByHandAt: string | null; sentByHandRef: string | null; payload: unknown };
  const columns: readonly Column<Row>[] = [
    { key: 'id', head: 'Message', cell: (r) => <>#{r.messageId}<div className="muted">{messageWords(r.kind, r.payload)}</div></> },
    { key: 'status', head: 'Status', cell: (r) => <Pill tone={r.status === 'failed' ? 'danger' : r.status === 'delivered' ? 'good' : r.status === 'awaiting_hand' ? 'testing' : 'empty'}>
        {r.status === 'awaiting_hand' ? 'waiting for a person' : r.status}</Pill> },
    { key: 'attempts', head: 'Tries', right: true, cell: (r) => count(r.attempts) },
    { key: 'why', head: 'What went wrong', cell: (r) => r.status === 'awaiting_hand'
        ? <span className="muted">nothing — no provider can send this kind, so a person must</span>
        : r.sentByHandHow
          ? <span className="muted">{howWords(r.sentByHandHow)}{r.sentByHandBy ? ` by ${r.sentByHandBy}` : ''}{r.sentByHandRef ? ` · ${r.sentByHandRef}` : ''}</span>
          : r.lastError ?? <span className="muted">—</span> },
    { key: 'when', head: 'When', cell: (r) => <>{r.createdAt}
        {r.status === 'awaiting_hand' && r.awaitingHandSince ? <div className="muted">waiting since {r.awaitingHandSince}</div>
          : r.nextTryAt ? <div className="muted">next try {r.nextTryAt}</div> : null}</> },
    { key: 'act', head: '', cell: (r) => r.status === 'awaiting_hand'
        ? (mayHand ? <Button small onClick={() => setSending((v) => v === r.messageId ? null : r.messageId)}>{sending === r.messageId ? 'Close' : 'I sent it'}</Button> : null)
        : may && r.status === 'failed'
          ? <Button small onClick={() => setRetrying((v) => v === r.messageId ? null : r.messageId)}>{retrying === r.messageId ? 'Close' : 'Send it again'}</Button>
          : null },
  ];

  return (
    <>
      {/* THE ANSWER FIRST, THE LIST UNDERNEATH (feedback: action and answer before
        * information). A person opening this tab wants to know whether anything is stuck,
        * and the list has never been able to tell him. */}
      <QueueHealth />
      <div className="toolbar">
        <Select name="status" label="Status" value={status} onChange={(v) => { setStatus(v as OutboxStatus | ''); setPage(1); }} placeholder="Any status"
                options={[{ value: 'awaiting_hand', label: 'Waiting for a person' }, { value: 'failed', label: 'Failed' }, { value: 'queued', label: 'Waiting its turn' }, { value: 'processing', label: 'Being sent' }, { value: 'delivered', label: 'Delivered' }, { value: 'recorded', label: 'Recorded (nobody acts on these)' }]} />
      </div>
      {/* NAMED, so a test can ask about THIS register and not whichever one is first in the
        * document. The health panel above is also a register, and the day it was added the
        * by-hand step started reading its rows — which is DEF-236 exactly: asking the right
        * question of the wrong element. */}
      <Card className="outbox-list">
        {asked.loading ? <Loading what="messages" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows}
                        empty={status === 'awaiting_hand' ? 'Nothing is waiting for a person to send it.'
                          : status === 'failed' ? 'Nothing has failed to send.' : 'No message matches that.'}
 asOf={new Date().toISOString()} />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
      {retrying !== null ? <Retry messageId={retrying} onDone={() => { setRetrying(null); asked.reload(); }} /> : null}
      {sending !== null ? <SentByHand messageId={sending} onDone={() => { setSending(null); asked.reload(); }} /> : null}
    </>
  );
}

/**
 * WHAT TO SAY, AND TO WHOM — in the office's words (0080).
 *
 * The queue holds the payload the command wrote, which names ids. A person
 * about to telephone a customer needs the sentence, not the record, so each
 * kind is read out here. An unknown kind falls back to its own name opened out
 * rather than being hidden: a new kind of message must be visible on the day it
 * is written.
 */
/**
 * WHAT A QUEUED MESSAGE IS, IN THE OFFICE'S WORDS.
 *
 * Keyed on the KIND now that a kind names the message (0124). It used to read
 * `payload.kind` first and fall back to the outer kind, because the outer one was a medium
 * — `whatsapp` — and said nothing about what the message was. The payloads no longer carry
 * an inner name; the outer one is the name.
 *
 * The old inner names are kept in the table below, deliberately: a database that upgraded
 * carries rows written before 0124, their payloads already stripped, but a developer's
 * machine or a restored backup may hold one that still has it. A message that renders as
 * its own identifier is a message nobody can act on.
 */
function messageWords(kind: string, payload: unknown): string {
  const p = (payload ?? {}) as { kind?: string; lendingId?: number; customerId?: number; billId?: number };
  const who = p.customerId ? ` for customer ${p.customerId}` : '';
  switch (kind) {
    case 'notice.365_day': case '365_notice':
      return `the 365-day notice${who} — the cylinder has been out a year and the replacement cost follows`;
    case 'bill.sent': case 'bill_sent': return `the month's bill${who}`;
    case 'bill.cost_raised': return `the replacement cost raised${who}`;
    case 'challan.issued': return `the challan issued${who}`;
    case 'sticker.qr': return 'the QR sticker for a cylinder — this is MADE here, not sent to anybody';
    case 'receipt.inward': return 'the inward receipt for a customer job — this is MADE here, not sent to anybody';
    case 'papers.issued': return 'a record that papers were issued — nobody has to act on this';
    case 'legacy.unnamed': return 'a message from before kinds were named, which could not be identified';
    default: return `${words(p.kind ?? kind)}${who}`;
  }
}

function howWords(how: string): string {
  const KNOWN: Record<string, string> = {
    whatsapp: 'sent on WhatsApp by hand', phone: 'read out on the telephone', email: 'sent by e-mail by hand',
    printed: 'printed and handed over or posted', in_person: 'told in person',
  };
  return KNOWN[how] ?? words(how);
}

/**
 * A PERSON SENT IT AND SAYS SO (0080). Nothing here pretends the system sent
 * anything: it records what somebody states he did, which means he used and a
 * reference it can be traced by, and keeps his name against it.
 */
function SentByHand({ messageId, onDone }: { messageId: number; onDone: () => void }) {
  const [how, setHow] = useState('');
  const [reference, setReference] = useState('');
  const [sentOn, setSentOn] = useState(today());
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('outbox.sendByHand', {
      messageId, how: how as 'whatsapp' | 'phone' | 'email' | 'printed' | 'in_person',
      ...(reference.trim() ? { reference: reference.trim() } : {}),
      ...(sentOn ? { sentOn } : {}),
    }));
    if (ok) onDone();
  }
  return (
    <Card title={`Message #${messageId} — you sent it`} sub="no provider is configured for this kind, so this records what a person actually did">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="info" title="Say how it went, not that it went."
              detail="This system cannot see a message leave your phone. Your name, the means and the day are kept with it, so a delivery by hand is never mistaken for one the system made." />
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Select name="how" label="How it was sent" value={how} onChange={setHow} placeholder="Choose"
                options={[{ value: 'whatsapp', label: 'WhatsApp' }, { value: 'phone', label: 'Telephone' },
                          { value: 'email', label: 'E-mail' }, { value: 'printed', label: 'Printed and handed over' },
                          { value: 'in_person', label: 'Told in person' }]} />
        <Text name="reference" label="Reference" value={reference} onChange={setReference}
              help="the number it went to, the docket, or who took the call" />
        <Text name="sentOn" label="The day it went" type="date" value={sentOn} onChange={setSentOn} />
      </div>
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !how}>
        {runner.busy ? 'Recording…' : 'Record that it was sent'}
      </Button>
    </Card>
  );
}

function Retry({ messageId, onDone }: { messageId: number; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('outbox.retry', { messageId, reason: reason.trim() }));
    if (ok) onDone();
  }
  return (
    <Card title={`Send message #${messageId} again`} sub="it goes back into the queue — the worker picks it up on its next sweep">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Banner kind="info" title="Check what went wrong before sending it again."
              detail="If no provider is configured, every retry will fail the same way — that is a setting, not this message." />
      <TextArea name="retry-reason" label="Why it is being sent again" value={reason} onChange={setReason} />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim()}>
        {runner.busy ? 'Queueing…' : 'Put it back in the queue'}
      </Button>
    </Card>
  );
}

function Jobs() {
  const jobs = useAsk(() => api.view('jobs.list', {}), [], MESSAGES);
  const periods = useAsk(() => api.view('jobperiods.list', { unfinishedOnly: true }), [], MESSAGES);
  const [open, setOpen] = useState<{ jobName: string; periodKey: string } | null>(null);

  return (
    <>
      <Card title="Scheduled work" sub="what runs on its own, how often, and whether the last run finished">
        {jobs.loading ? <Loading what="the jobs" /> : jobs.error ? <Banner kind="error" title={jobs.error.title} {...(jobs.error.detail ? { detail: jobs.error.detail } : {})} />
          : <Register
              columns={[
                { key: 'name', head: 'Job', cell: (j: { name: string; description: string }) => <><strong>{j.name}</strong><div className="muted">{j.description}</div></> },
                { key: 'every', head: 'Every', right: true, cell: (j: { everySeconds: number }) => everyWords(j.everySeconds) },
                { key: 'last', head: 'Last run', cell: (j: { lastRunAt: string | null; lastFinishedAt: string | null }) => j.lastRunAt ? <>{j.lastRunAt}{j.lastFinishedAt ? null : <div className="muted">started and not finished</div>}</> : <span className="muted">never</span> },
                { key: 'status', head: 'Outcome', cell: (j: { lastStatus: string | null; lastError: string | null }) => j.lastStatus
                    ? <><Pill tone={j.lastStatus === 'ok' ? 'good' : 'danger'}>{j.lastStatus}</Pill>{j.lastError ? <div className="muted">{j.lastError}</div> : null}</>
                    : <span className="muted">—</span> },
                { key: 'runs', head: 'Runs / failures', right: true, cell: (j: { runs: number; failures: number }) => <>{count(j.runs)} / <strong>{count(j.failures)}</strong></> },
                { key: 'enabled', head: 'Enabled', cell: (j: { enabled: boolean }) => <Pill tone={j.enabled ? 'good' : 'muted'}>{j.enabled ? 'yes' : 'no'}</Pill> },
              ]}
              rows={jobs.data!.rows} empty="No scheduled work is configured."
              asOf={instant(new Date().toISOString())} />}
      </Card>

      <Card title="Periods still owed" sub="a job that runs per period owes work for each one until it finishes it">
        {periods.loading ? <Loading what="the periods" /> : periods.error ? <Banner kind="error" title={periods.error.title} {...(periods.error.detail ? { detail: periods.error.detail } : {})} />
          : periods.data!.rows.length === 0
            ? <Banner kind="good" title="Every period every job owes has been finished." />
            : <Register
                columns={[
                  { key: 'job', head: 'Job', cell: (p: { jobName: string }) => p.jobName },
                  { key: 'period', head: 'Period', cell: (p: { periodKey: string }) => <strong>{p.periodKey}</strong> },
                  { key: 'status', head: 'Status', cell: (p: { status: string; leaseExpired: boolean }) => <><Pill tone={p.status === 'done' ? 'good' : 'empty'}>{p.status}</Pill>{p.leaseExpired ? <> <Pill tone="danger">claim expired</Pill></> : null}</> },
                  { key: 'tries', head: 'Attempts', right: true, cell: (p: { attempts: number }) => count(p.attempts) },
                  { key: 'why', head: 'What went wrong', cell: (p: { lastError: string | null }) => p.lastError ?? <span className="muted">—</span> },
                  { key: 'open', head: '', cell: (p: { jobName: string; periodKey: string }) => <Button small onClick={() => setOpen({ jobName: p.jobName, periodKey: p.periodKey })}>Every attempt</Button> },
                ]}
                rows={periods.data!.rows} empty="none" />}
      </Card>

      {open ? <Attempts jobName={open.jobName} periodKey={open.periodKey} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

function Attempts({ jobName, periodKey, onClose }: { jobName: string; periodKey: string; onClose: () => void }) {
  const asked = useAsk(() => api.view('jobperiods.attempts', { jobName, periodKey }), [jobName, periodKey], MESSAGES);
  return (
    <Card title={`${jobName} — ${periodKey}`} sub="every attempt, in order, including one worker taking over another's expired claim"
          actions={<Button small onClick={onClose}>Close</Button>}>
      {asked.loading ? <Loading what="the attempts" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
        : <Register
            columns={[
              { key: 'no', head: 'Attempt', right: true, cell: (a: { attemptNo: number }) => `#${a.attemptNo}` },
              { key: 'worker', head: 'Worker', cell: (a: { worker: string | null; tookOverAnExpiredClaim: boolean }) => <>{a.worker ?? '—'}{a.tookOverAnExpiredClaim ? <div className="muted">took over an expired claim</div> : null}</> },
              { key: 'started', head: 'Started', cell: (a: { startedAt: string }) => a.startedAt },
              { key: 'ended', head: 'Ended', cell: (a: { endedAt: string | null }) => a.endedAt ?? <span className="muted">still running</span> },
              { key: 'outcome', head: 'Outcome', cell: (a: { outcome: string | null; error: string | null }) => a.outcome
                  ? <><Pill tone={a.outcome === 'ok' ? 'good' : 'danger'}>{a.outcome}</Pill>{a.error ? <div className="muted">{a.error}</div> : null}</>
                  : <span className="muted">—</span> },
            ]}
            rows={asked.data!.rows} empty="No attempt has been recorded for this period." />}
    </Card>
  );
}
